import { IonIcon, IonLabel, IonList, IonText } from '@ionic/react';
import { heart, heartOutline } from 'ionicons/icons';
import { isNil, isObject, orderBy } from 'lodash';
import { ReactElement, memo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Machine } from '../../api/graphql/generated';
import { MachineVariables } from '../../api/hooks/binding/binding.types';
import { useMachineVariables } from '../../api/hooks/machine-variable.hooks';
import {
  useFavoritesHelpers,
  useMachineVariableFavorites,
} from '../../store/favorites/favorites.hooks';
import {
  URVariable,
  stringifyURVariable,
} from '../../util/machine-variable.utils';
import { IonItem } from '../ionic/IonItem';
import { Collapsible } from '../ui/collapsible';
import { Highlighter } from './highlighter';
import './machine.css';

interface MachineDetailsProps {
  machine?: Machine;
}

interface Variable {
  name: string;
  value: URVariable;
}

const MachineVariablesComponent = (
  props: MachineDetailsProps
): ReactElement => {
  const { machine } = props;
  const { i18n } = useTranslation();

  const variables = useMachineVariables({ id: machine?.id });
  const variablesArray = orderBy(
    convertVariablesToArray(variables),
    ['name'],
    ['asc']
  );

  const favVariables = useMachineVariableFavorites(machine);
  const { addMachineFavoriteVariable, removeMachineFavoriteVariable } =
    useFavoritesHelpers();

  const toggleFavorite = (name: string) => {
    if (machine) {
      if (favVariables?.includes(name)) {
        removeMachineFavoriteVariable({ favorite: name, machine });
      } else {
        addMachineFavoriteVariable({ favorite: name, machine });
      }
    }
  };

  if (variablesArray.length === 0) {
    return (
      <div className="full-height-width content-center">
        <IonLabel>
          {i18n.t('common:machine_variables_waiting_for_data')}
        </IonLabel>
      </div>
    );
  } else {
    return (
      <IonList lines="full">
        {variablesArray.map((v) => {
          return (
            <MachineVariable
              key={v.name}
              variable={v}
              isFavorited={favVariables?.includes(v.name) ?? false}
              onFavorite={toggleFavorite}
            />
          );
        })}
      </IonList>
    );
  }
};

const MachineVariablesComponentMemo = memo(MachineVariablesComponent);

interface MachineVariableProps {
  variable: Variable;
  isFavorited: boolean;
  onFavorite: (name: string) => void;
}

const MachineVariable = (props: MachineVariableProps): ReactElement => {
  const { variable, isFavorited, onFavorite } = props;
  const [expanded, setExpanded] = useState<boolean>(false);

  const valueRef = useRef<HTMLIonLabelElement>(null);
  let isEllipsedValue = false;
  if (valueRef.current) {
    const { offsetWidth, scrollWidth } = valueRef.current;
    if (!isNil(offsetWidth) && !isNil(scrollWidth)) {
      isEllipsedValue = offsetWidth < scrollWidth;
    }
  }

  const value = getVariableStringValue(variable);
  return (
    <Highlighter value={value}>
      <IonItem
        className="transparent-background"
        onClick={() => setExpanded(!expanded)}
      >
        <IonIcon
          className="ion-padding-end"
          color={isFavorited ? 'danger' : 'medium'}
          icon={isFavorited ? heart : heartOutline}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onFavorite(variable.name);
          }}
        />
        <IonLabel>{variable.name}</IonLabel>
        <IonLabel
          ref={valueRef}
          className="ion-text-end"
          color="medium"
          slot="end"
        >
          {value}
        </IonLabel>
      </IonItem>
      {isEllipsedValue && (
        <Collapsible collapsed={!expanded}>
          <IonItem className="expanded-variable">
            <IonText color="medium" slot="end">
              {value}
            </IonText>
          </IonItem>
        </Collapsible>
      )}
    </Highlighter>
  );
};

const getVariableStringValue = (variable: Variable): string | undefined => {
  return isObject(variable.value)
    ? stringifyURVariable(variable.value)
    : String(variable.value);
};

const convertVariablesToArray = (variables: MachineVariables): Variable[] => {
  const varArray: Variable[] = [];

  Object.entries(variables).forEach(([name, value]) => {
    varArray.push({ name, value });
  });
  return varArray;
};

export { MachineVariablesComponentMemo as MachineVariables };
